import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  QrCode,
  Check,
  X,
  Clock,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Phone,
  Calendar
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { API } from '../../App';

const AdminQRCodes = () => {
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [extendModal, setExtendModal] = useState(null);
  const [extendDays, setExtendDays] = useState(365);

  const fetchQRCodes = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.append('status', statusFilter);
      if (subscriptionFilter) params.append('subscription', subscriptionFilter);

      const response = await axios.get(`${API}/admin/qr-codes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setQrCodes(response.data.qr_codes);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, subscriptionFilter]);

  const handleQRAction = async (qrCodeId, action, extraData = {}) => {
    setActionLoading(qrCodeId);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/qr-codes/action`, 
        { qr_code_id: qrCodeId, action, ...extraData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`QR code ${action} successfully`);
      fetchQRCodes();
      setExtendModal(null);
    } catch (error) {
      toast.error(`Failed to ${action} QR code`);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePaymentOverride = async (qrCodeId, action) => {
    setActionLoading(qrCodeId);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/payment/override`, 
        { qr_code_id: qrCodeId, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Payment ${action} successfully`);
      fetchQRCodes();
    } catch (error) {
      toast.error(`Failed to override payment`);
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    fetchQRCodes();
  }, [fetchQRCodes]);

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">QR Code Management</h1>
          <p className="text-slate-400">Manage QR codes and subscriptions</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="qr-status-filter"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>

          <select
            value={subscriptionFilter}
            onChange={(e) => { setSubscriptionFilter(e.target.value); setPage(1); }}
            className="bg-slate-800 border border-slate-700 text-white rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="qr-subscription-filter"
          >
            <option value="">All Types</option>
            <option value="free">Free</option>
            <option value="paid">Paid</option>
            <option value="expired">Expired</option>
          </select>

          <div className="ml-auto text-slate-400">
            Total: {total} QR codes
          </div>
        </div>

        {/* QR Codes Table */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800">
                <tr>
                  <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">QR Code</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">Vehicle</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">Owner</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">Type</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">Messages</th>
                  <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">Expires</th>
                  <th className="text-right text-slate-400 font-medium px-6 py-4 text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center">
                      <Loader2 className="w-6 h-6 text-slate-500 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : qrCodes.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-slate-400">
                      No QR codes found
                    </td>
                  </tr>
                ) : (
                  qrCodes.map((qr) => (
                    <tr key={qr.id} className="hover:bg-slate-800/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <QrCode className="w-4 h-4 text-slate-500" />
                          <span className="text-white font-mono text-sm">{qr.code.substring(0, 12)}...</span>
                        </div>
                        {qr.disabled_by_admin && (
                          <span className="text-xs text-red-400 mt-1 block">Disabled by admin</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-white">{qr.car_nickname || 'N/A'}</p>
                        <p className="text-slate-400 text-sm">{qr.vehicle_number || 'No number'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-300 font-mono text-sm">{qr.owner_phone_masked || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          qr.is_paid
                            ? isExpired(qr.expires_at)
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-amber-500/20 text-amber-400'
                            : 'bg-slate-500/20 text-slate-400'
                        }`}>
                          {qr.is_paid ? (isExpired(qr.expires_at) ? 'Expired' : 'Paid') : 'Free'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-white">{qr.message_count || 0}</td>
                      <td className="px-6 py-4 text-slate-400 text-sm">
                        {qr.expires_at 
                          ? new Date(qr.expires_at).toLocaleDateString('en-IN')
                          : '-'
                        }
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {!qr.is_paid && (
                            <button
                              onClick={() => handlePaymentOverride(qr.id, 'mark_paid')}
                              disabled={actionLoading === qr.id}
                              className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-all disabled:opacity-50"
                              title="Mark as Paid"
                              data-testid={`mark-paid-${qr.id}`}
                            >
                              {actionLoading === qr.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {qr.is_paid && (
                            <button
                              onClick={() => setExtendModal(qr.id)}
                              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-all"
                              title="Extend Subscription"
                              data-testid={`extend-${qr.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          )}
                          {!qr.disabled_by_admin && qr.is_active ? (
                            <button
                              onClick={() => handleQRAction(qr.id, 'disable')}
                              disabled={actionLoading === qr.id}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-50"
                              title="Disable QR"
                              data-testid={`disable-qr-${qr.id}`}
                            >
                              {actionLoading === qr.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleQRAction(qr.id, 'enable')}
                              disabled={actionLoading === qr.id}
                              className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-all disabled:opacity-50"
                              title="Enable QR"
                              data-testid={`enable-qr-${qr.id}`}
                            >
                              {actionLoading === qr.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="text-slate-400">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Extend Subscription Modal */}
        {extendModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md">
              <div className="p-6 border-b border-slate-700">
                <h2 className="text-xl font-bold text-white">Extend Subscription</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Extension Period (days)</label>
                  <input
                    type="number"
                    value={extendDays}
                    onChange={(e) => setExtendDays(parseInt(e.target.value) || 365)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white"
                    min="1"
                    max="3650"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setExtendModal(null)}
                    className="flex-1 px-4 py-3 border border-slate-600 text-slate-300 rounded-xl hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleQRAction(extendModal, 'extend', { extend_days: extendDays })}
                    disabled={actionLoading === extendModal}
                    className="flex-1 px-4 py-3 bg-amber-500 text-slate-900 font-semibold rounded-xl hover:bg-amber-600 disabled:opacity-50"
                  >
                    {actionLoading === extendModal ? 'Extending...' : 'Extend'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminQRCodes;
